// Pure math helpers for the Abacus tool: number <-> soroban rod conversions
// and problem generators for the mental-math and test modes.

export function randInt(min, max) {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

// A soroban rod is { heaven: bool (worth 5), earth: 0..4 (worth 1 each) }.
export function rodDigit(rod) {
  return (rod.heaven ? 5 : 0) + (rod.earth || 0);
}

export function emptyRods(rodCount) {
  return Array.from({ length: rodCount }, () => ({ heaven: false, earth: 0 }));
}

// Most-significant rod first.
export function numberToRods(value, rodCount) {
  const rods = emptyRods(rodCount);
  let v = Math.max(0, Math.floor(Number(value) || 0));
  // If it overflows the available rods, keep the low-order digits.
  const digits = [];
  for (let i = 0; i < rodCount; i++) {
    digits.push(v % 10);
    v = Math.floor(v / 10);
  }
  digits.reverse();
  return rods.map((_, i) => ({ heaven: digits[i] >= 5, earth: digits[i] % 5 }));
}

export function rodsToNumber(rods) {
  return rods.reduce((acc, r) => acc * 10 + rodDigit(r), 0);
}

export function maxValueForRods(rodCount) {
  return Math.pow(10, rodCount) - 1;
}

export function formatNumber(n) {
  const num = Number(n) || 0;
  return num.toLocaleString("en-US");
}

// ── Mental-math / flash-anzan sequence ────────────────────────────────────
// Produces a list of signed terms whose running total never goes negative and
// never exceeds what the abacus can display.
export function generateFlashSequence({
  terms = 5,
  digits = 1,
  allowSubtraction = false,
  maxRods = 5,
}) {
  const cap = maxValueForRods(maxRods);
  const hi = Math.min(Math.pow(10, digits) - 1, cap);
  const lo = digits <= 1 ? 1 : Math.pow(10, digits - 1);
  const sequence = [];
  let total = 0;
  let guard = 0;

  while (sequence.length < terms && guard < terms * 40) {
    guard += 1;
    if (sequence.length === 0) {
      const n = randInt(lo, Math.max(lo, Math.min(hi, cap)));
      total = n;
      sequence.push(n);
      continue;
    }
    const canSub = allowSubtraction && total > lo;
    const wantSub = canSub && Math.random() < 0.42;
    if (wantSub) {
      const n = randInt(lo, Math.min(hi, total));
      total -= n;
      sequence.push(-n);
    } else {
      let n = randInt(lo, hi);
      if (total + n > cap) {
        n = cap - total;
        if (n < lo) continue; // no room, retry (maybe a subtraction next loop)
      }
      total += n;
      sequence.push(n);
    }
  }
  return { sequence, answer: total };
}

// ── Operation-based flash sequence (add / subtract / multiply / divide) ────
// Returns { items: [{ op, value }], answer }. The first item has op === null.
export function operationSymbol(op) {
  switch (op) {
    case "add": return "+";
    case "subtract": return "−";
    case "multiply": return "×";
    case "divide": return "÷";
    default: return "+";
  }
}

export function generateOperationSequence({
  terms = 5,
  min = 1,
  max = 9,
  operation = "add",
}) {
  let lo = Math.floor(Number(min));
  let hi = Math.floor(Number(max));
  if (!Number.isFinite(lo)) lo = 1;
  if (!Number.isFinite(hi)) hi = 9;
  if (lo > hi) [lo, hi] = [hi, lo];
  lo = Math.max(0, lo);
  hi = Math.max(lo, hi);

  const pick = () => randInt(lo, hi);
  const pickNonZero = () => {
    const base = Math.max(1, lo);
    return randInt(base, Math.max(base, hi));
  };

  const n = Math.max(1, Math.floor(terms));
  const items = [];

  // Division is built backwards so every step divides evenly.
  if (operation === "divide") {
    const divCount = n > 1 ? n - 1 : 0;
    const dlo = Math.max(2, lo);
    const dhi = Math.max(dlo, hi);
    const divisors = Array.from({ length: divCount }, () => randInt(dlo, dhi));
    const quotient = pickNonZero();
    const start = divisors.reduce((acc, d) => acc * d, quotient);
    items.push({ op: null, value: start });
    divisors.forEach((d) => items.push({ op: "÷", value: d }));
    return { items, answer: quotient };
  }

  let result = 0;
  for (let i = 0; i < n; i++) {
    if (i === 0) {
      const v = operation === "multiply" ? pickNonZero() : pick();
      result = v;
      items.push({ op: null, value: v });
      continue;
    }

    let op = operation === "mixed"
      ? ["+", "−", "×", "÷"][randInt(0, 3)]
      : operationSymbol(operation);

    if (op === "+") {
      const v = pick();
      result += v;
      items.push({ op, value: v });
    } else if (op === "−") {
      let v = pick();
      if (v > result) v = result; // never go negative
      result -= v;
      items.push({ op, value: v });
    } else if (op === "×") {
      const v = pickNonZero();
      result *= v;
      items.push({ op, value: v });
    } else if (op === "÷") {
      const dlo = Math.max(2, lo);
      const dhi = Math.max(dlo, hi);
      const divisors = [];
      for (let d = dlo; d <= dhi; d++) {
        if (result !== 0 && result % d === 0) divisors.push(d);
      }
      if (divisors.length) {
        const v = divisors[randInt(0, divisors.length - 1)];
        result /= v;
        items.push({ op: "÷", value: v });
      } else {
        // no clean divisor available — multiply instead so mixed stays valid
        const v = pickNonZero();
        result *= v;
        items.push({ op: "×", value: v });
      }
    }
  }
  return { items, answer: result };
}

// Running totals after each item, for mirroring on the abacus.
export function partialResults(items) {
  let acc = 0;
  return items.map((it, i) => {
    if (i === 0) { acc = it.value; return acc; }
    if (it.op === "+") acc += it.value;
    else if (it.op === "−") acc -= it.value;
    else if (it.op === "×") acc *= it.value;
    else if (it.op === "÷") acc = it.value === 0 ? acc : acc / it.value;
    return acc;
  });
}

export function itemsToExpression(items) {
  return items
    .map((it, i) => (i === 0 ? formatNumber(it.value) : `${it.op} ${formatNumber(it.value)}`))
    .join(" ");
}

// ── Written arithmetic problem for the typing test ────────────────────────
const OPERATIONS = ["add", "subtract", "multiply", "divide"];

export function generateArithmeticProblem({ operation = "add", digits = 2 }) {
  const op = operation === "mixed"
    ? OPERATIONS[randInt(0, OPERATIONS.length - 1)]
    : operation;
  const hi = Math.pow(10, digits) - 1;
  const lo = digits <= 1 ? 1 : Math.pow(10, digits - 1);

  let a;
  let b;
  let answer;
  let symbol;

  switch (op) {
    case "subtract": {
      a = randInt(lo, hi);
      b = randInt(lo, hi);
      if (b > a) [a, b] = [b, a];
      answer = a - b;
      symbol = "−";
      break;
    }
    case "multiply": {
      const mHi = Math.max(9, Math.floor(hi / 3));
      a = randInt(2, mHi);
      b = randInt(2, Math.max(2, Math.min(mHi, 12 + digits * 3)));
      answer = a * b;
      symbol = "×";
      break;
    }
    case "divide": {
      b = randInt(2, Math.max(2, Math.min(hi, 12 + digits * 3)));
      answer = randInt(2, Math.max(2, Math.floor(hi / b) || 2));
      a = b * answer;
      symbol = "÷";
      break;
    }
    case "add":
    default: {
      a = randInt(lo, hi);
      b = randInt(lo, hi);
      answer = a + b;
      symbol = "+";
      break;
    }
  }

  return {
    a,
    b,
    op,
    symbol,
    answer,
    text: `${formatNumber(a)} ${symbol} ${formatNumber(b)}`,
  };
}

export function operationLabel(op) {
  switch (op) {
    case "add": return "Addition";
    case "subtract": return "Subtraction";
    case "multiply": return "Multiplication";
    case "divide": return "Division";
    case "mixed": return "Mixed";
    default: return op;
  }
}
